import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { 
  Package, 
  Upload, 
  CheckCircle, 
  Clock, 
  AlertTriangle, 
  Calendar,
} from 'lucide-react';
import { useJob } from '@/hooks/useJobs';
import { usePurchaseOrders } from '@/hooks/usePurchaseOrders';
import { useDeliveryProofs } from '@/hooks/useDeliveryProofs';
import { formatCurrency } from '@/lib/types/job';
import { DeliverySubmissionForm } from '@/components/delivery/DeliverySubmissionForm';
import { DeliveryReviewInterface } from '@/components/delivery/DeliveryReviewInterface';
import { format } from 'date-fns';

export default function JobExecutionPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const { job, loading: jobLoading } = useJob(jobId!);
  const { purchaseOrders } = usePurchaseOrders();
  const { deliveryProofs } = useDeliveryProofs({ jobId });
  const [showDeliveryForm, setShowDeliveryForm] = useState(false);

  if (jobLoading) {
    return <div className="p-6">Loading job details...</div>;
  }

  if (!job) {
    return <div className="p-6">Job not found</div>;
  }

  const activePO = purchaseOrders?.find(po => po.job_id === jobId && po.status === 'acknowledged');
  const latestDeliveryProof = deliveryProofs?.[0];
  const canSubmitDelivery = job.status === 'in_progress' && !latestDeliveryProof;

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-2">Job Execution: {job.title}</h1>
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <span>Job ID: {job.public_id}</span>
          <span>Budget: {formatCurrency(job.budget_cents)}</span>
          <Badge variant="outline">{job.status}</Badge>
        </div>
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="delivery">Delivery Proof</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                Execution Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <p className="text-muted-foreground">
                  Job Status: <Badge variant="outline">{job.status}</Badge>
                </p>
                
                {canSubmitDelivery && (
                  <Dialog open={showDeliveryForm} onOpenChange={setShowDeliveryForm}>
                    <DialogTrigger asChild>
                      <Button className="gap-2">
                        <Upload className="h-4 w-4" />
                        Submit Delivery Proof
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>Submit Delivery Proof</DialogTitle>
                        <DialogDescription>
                          Provide comprehensive documentation of your completed delivery
                        </DialogDescription>
                      </DialogHeader>
                      <DeliverySubmissionForm
                        jobId={job.id}
                        purchaseOrderId={activePO?.id}
                        onSubmissionComplete={() => {
                          setShowDeliveryForm(false);
                          window.location.reload();
                        }}
                      />
                    </DialogContent>
                  </Dialog>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="delivery">
          {latestDeliveryProof ? (
            <DeliveryReviewInterface
              deliveryProof={latestDeliveryProof}
              onReviewComplete={() => window.location.reload()}
            />
          ) : (
            <Card>
              <CardContent className="text-center py-8">
                <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No Delivery Proof Submitted</h3>
                <p className="text-muted-foreground">
                  Submit delivery documentation when your work is complete.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}